import React from "react";
import { motion } from "framer-motion";
import { awards, awardsHeader } from "../portfolio";
import { blurIn, staggerContainer, fadeIn } from "../utils/motion";
import { FiArrowUpRight, FiAward, FiFileText } from "react-icons/fi";
import SpotlightCard from "./SpotlightCard";
import ScrambleText from "./ScrambleText";
import { playClick, playHover } from "../utils/soundEngine";

function AwardCard({ item, index }) {
  const logoPath = (() => {
    try {
      return require(`../assests/images/${item.logo_path}`);
    } catch {
      return null;
    }
  })();

  const isPublication =
    item.type?.toLowerCase().includes("publication") ||
    item.type?.toLowerCase().includes("paper");

  return (
    <motion.div variants={fadeIn("up", index * 0.1)} className="h-full">
      <SpotlightCard
        className="group relative flex flex-col justify-between h-full p-6 sm:p-7 rounded-2xl border border-surface/[0.08] bg-surface/[0.015] hover:bg-surface/[0.035] hover:border-surface/[0.16] transition-all duration-400 ease-out-expo shadow-[0_4px_24px_rgba(0,0,0,0.04)]"
        spotlightColor="rgba(var(--color-surface), 0.05)"
      >
        {/* Subtle Ambient Hover Glow */}
        <div className="absolute top-0 right-0 w-32 h-32 bg-surface/[0.03] rounded-full blur-2xl pointer-events-none group-hover:bg-surface/[0.06] transition-colors duration-500" />

        <div>
          {/* Header Row: Logo / Icon & Badge */}
          <div className="flex items-start justify-between gap-2.5 mb-4 flex-wrap">
            <div className="flex items-center gap-3 min-w-0 flex-1">
              <div className="w-10 h-10 rounded-xl border border-surface/[0.08] bg-bg-raised flex items-center justify-center overflow-hidden p-1.5 shrink-0 group-hover:border-surface/[0.18] transition-colors duration-300">
                {logoPath ? (
                  <img
                    src={logoPath}
                    alt={item.issuer}
                    className="w-full h-full object-contain"
                  />
                ) : isPublication ? (
                  <FiFileText size={18} className="text-fg-muted" />
                ) : (
                  <FiAward size={18} className="text-fg-muted" />
                )}
              </div>
              <div className="min-w-0 flex-1">
                <span className="block font-mono text-[10.5px] tracking-wider uppercase text-fg-faint truncate">
                  {item.issuer}
                </span>
                <span className="block font-mono text-[10px] text-fg-ghost">
                  {item.date}
                </span>
              </div>
            </div>

            <span className="px-2.5 py-0.5 rounded-full bg-surface/[0.04] border border-surface/[0.06] text-[10px] font-mono text-fg-muted whitespace-nowrap self-start mt-0.5">
              {item.type}
            </span>
          </div>

          {/* Title */}
          <h3 className="font-display text-base sm:text-lg font-bold text-fg mb-2.5 leading-snug group-hover:text-fg transition-colors duration-300">
            {item.title}
          </h3>

          {/* Metrics */}
          {item.metrics && item.metrics.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-3.5">
              {item.metrics.map((metric, mIdx) => (
                <span
                  key={mIdx}
                  className="px-2 py-0.5 rounded bg-surface/[0.03] border border-surface/[0.05] text-[10.5px] font-mono text-fg-faint"
                >
                  {metric}
                </span>
              ))}
            </div>
          )}

          {/* Description */}
          <p className="text-xs sm:text-sm text-fg-muted leading-relaxed mb-6">
            {item.description}
          </p>
        </div>

        {/* Footer */}
        {item.url && item.url.trim() && (
          <div className="pt-4 border-t border-surface/[0.06]">
            <a
              href={item.url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => playClick()}
              onMouseEnter={() => playHover()}
              className="inline-flex items-center gap-1.5 text-xs sm:text-sm font-medium text-fg group-hover:text-fg-muted transition-colors duration-300"
            >
              {isPublication ? "View Publication" : "Visit Organization"}
              <FiArrowUpRight
                size={14}
                className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
              />
            </a>
          </div>
        )}
      </SpotlightCard>
    </motion.div>
  );
}

function Awards() {
  return (
    <motion.div
      variants={staggerContainer()}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, amount: 0.05 }}
    >
      <motion.div variants={blurIn("up")} className="mb-12 sm:mb-14">
        <span className="section-label">Distinctions</span>
        <h2 className="section-title">
          <ScrambleText text={awardsHeader.title} hover={true} />
        </h2>
        <p className="section-subtitle">{awardsHeader.description}</p>
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {awards.data.map((item, i) => (
          <AwardCard key={item.title} item={item} index={i} />
        ))}
      </div>
    </motion.div>
  );
}

export default Awards;
